import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, MapPin, Headphones, Image, Edit, Trash2, Eye, EyeOff, Play, Pause, Save } from 'lucide-react';
import { Tour, TourPoint } from '../../types';
import Button from '../UI/Button';
import apiService from '../../services/api';
import toast from 'react-hot-toast';
import TourStopCreationForm from './TourStopCreationForm';
import { extractTourText } from '../../utils/tourUtils';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface TourPointManagerProps {
  tour: Tour;
  onClose: () => void;
}

interface SortableTourPointCardProps {
  point: TourPoint;
  index: number;
  onEdit: (point: TourPoint) => void;
  onDelete: (pointId: number) => void;
}

const SortableTourPointCard: React.FC<SortableTourPointCardProps> = ({ point, index, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: point.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow ${isDragging ? 'cursor-grabbing opacity-50' : 'cursor-grab'}`}
    >
      {/* Thumbnail Image */}
      {point.thumbnailUrl ? (
        <div className="w-full h-32 bg-gray-100">
          <img
            src={point.thumbnailUrl}
            alt={`${point.name ? extractTourText(point.name) : `Point ${index + 1}`} thumbnail`}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          />
          <div className="hidden w-full h-full bg-gray-200 flex items-center justify-center">
            <Image className="h-8 w-8 text-gray-400" />
          </div>
        </div>
      ) : (
        <div className="w-full h-32 bg-gray-200 flex items-center justify-center">
          <Image className="h-8 w-8 text-gray-400" />
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center flex-1">
            <div className="w-8 h-8 bg-primary border border-gray-200 text-gray-900 rounded-full flex items-center justify-center text-sm font-medium mr-3">
              {index + 1}
            </div>
            <h4 className="font-medium text-gray-900">{point.name ? extractTourText(point.name) : `Point ${index + 1}`}</h4>
          </div>
          <div className="flex space-x-1" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(point);
              }}
              title="Edit Point"
            >
              <Edit className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(point.id);
              }}
              className="text-red-600 hover:text-red-700"
              title="Delete Point"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <p className="text-gray-600 line-clamp-2">
            {point.discription ? extractTourText(point.discription) : 'No description'}
          </p>
          
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Radius: {point.radius}m</span>
            <span>
              {point.lattitude || 'N/A'}, {point.longitude || 'N/A'}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            {point.mainAudioUrl && (
              <div className="flex items-center text-xs text-blue-600">
                <Headphones className="h-3 w-3 mr-1" />
                Stop Audio
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {point.isHidePreview === 'true' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                <EyeOff className="h-3 w-3 mr-1" />
                Hidden from Preview
              </span>
            )}
            {point.isHideStop === 'true' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                <EyeOff className="h-3 w-3 mr-1" />
                Hidden Stop
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TourPointManager: React.FC<TourPointManagerProps> = ({ tour, onClose }) => {
  const [tourPoints, setTourPoints] = useState<TourPoint[]>([]);
  const [showPointForm, setShowPointForm] = useState(false);
  const [editingPoint, setEditingPoint] = useState<TourPoint | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [originalOrder, setOriginalOrder] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    loadTourPoints();
  }, [tour.id]);

  const loadTourPoints = async () => {
    try {
      setIsLoading(true);
      const points = await apiService.getTourPoints(tour.id);
      setTourPoints(points);
      setOriginalOrder(points.map(p => p.id));
    } catch (error) {
      toast.error('Failed to load tour points');
    } finally {
      setIsLoading(false);
    }
  };

  const hasOrderChanged = useMemo(() => {
    if (tourPoints.length !== originalOrder.length) return false;
    return tourPoints.some((point, index) => point.id !== originalOrder[index]);
  }, [tourPoints, originalOrder]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setTourPoints((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveOrder = async () => {
    try {
      setIsSaving(true);
      const stopIds = tourPoints.map(point => point.id);
      await apiService.reorderTourStops(tour.id, stopIds);
      // Reload tour points to get updated order from backend
      await loadTourPoints();
      toast.success('Tour stops order saved successfully');
    } catch (error) {
      toast.error('Failed to save tour stops order');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePoint = async (pointId: number) => {
    if (window.confirm('Are you sure you want to delete this tour point?')) {
      try {
        await apiService.deleteTourPoint(pointId, tour.id);
        toast.success('Tour point deleted successfully');
        await loadTourPoints();
      } catch (error) {
        toast.error('Failed to delete tour point');
      }
    }
  };

  const handleEditPoint = (point: TourPoint) => {
    setEditingPoint(point);
    setShowPointForm(true);
  };

  const handleAddPoint = () => {
    setEditingPoint(null);
    setShowPointForm(true);
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Manage Tour Points - {extractTourText(tour.name)}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Add, edit, and manage tour points with their own images, audio, and stop information
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {hasOrderChanged && (
              <Button
                onClick={handleSaveOrder}
                size="sm"
                disabled={isSaving}
                className="flex items-center bg-green-600 hover:bg-green-700"
              >
                <Save className="h-4 w-4 mr-1" />
                {isSaving ? 'Saving...' : 'Save Order'}
              </Button>
            )}
            <Button onClick={handleAddPoint} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Add Point
            </Button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {tourPoints.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No tour points yet</h3>
                  <p className="text-gray-600 mb-4">
                    Start by adding your first tour point with its own image, audio, and stop information
                  </p>
                  <Button onClick={handleAddPoint}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Point
                  </Button>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={tourPoints.map(point => point.id)}
                    strategy={rectSortingStrategy}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {tourPoints.map((point, index) => (
                        <SortableTourPointCard
                          key={point.id}
                          point={point}
                          index={index}
                          onEdit={handleEditPoint}
                          onDelete={handleDeletePoint}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          )}
        </div>

        {/* Tour Point Form Modal */}
        {showPointForm && (
          <TourStopCreationForm
            tourId={tour.id}
            isEditing={!!editingPoint}
            initialData={editingPoint || undefined}
            onClose={() => setShowPointForm(false)}
            onSuccess={(tourStop) => {
              if (editingPoint) {
                setTourPoints(prev => prev.map(p => p.id === tourStop.id ? tourStop : p));
              } else {
                setTourPoints(prev => [...prev, tourStop]);
                setOriginalOrder(prev => [...prev, tourStop.id]);
              }
              setShowPointForm(false);
              setEditingPoint(null);
              toast.success(editingPoint ? 'Tour stop updated successfully' : 'Tour stop created successfully');
            }}
          />
        )}
      </div>
    </div>
  );
};

export default TourPointManager;
